/* eslint-disable jsx-a11y/label-has-associated-control */
import FieldWrapper from "@/components/FieldWrapper";
import { colors, spacing } from "@/config/theme";
import { useGetReportSites, useGetValveTestHistory } from "@/reports/api";
import { Location, LOCATIONS } from "@/reports/config/locations";
import { exportValveTestHistory } from "@/reports/utils/psvExport";
import styled from "@emotion/styled";
import { message, Modal, Select } from "antd";
import dayjs from "dayjs";
import { useState } from "react";

interface Props {
  open: boolean;
  onClose: () => unknown;
  location?: Location;
}

const PSVExportNote = styled.p`
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 14px;
`;

const NoDataNote = styled(PSVExportNote)`
  color: ${colors.gray[700]};
`;
const NotesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xxs};
`;

const PSVExportModal = ({ onClose, open, location }: Props) => {
  const [siteName, setSelectedSiteName] = useState<string | undefined>();
  const [years, setYears] = useState<number | undefined>();
  const { data } = useGetReportSites({
    location,
  });
  const endDate = dayjs();
  const startDate = years
    ? endDate.subtract(years, "years").format()
    : undefined;
  const { data: reports, isFetching } = useGetValveTestHistory({
    location,
    siteName,
    startDate,
  });
  const isEmpty = !isFetching && reports && reports.length === 0;

  const onExport = async () => {
    if (!siteName) {
      message.error("Please select a site first");
      return;
    }
    if (!years) {
      message.error("Please select a date range");
      return;
    }
    if (!location) {
      message.error(`Invalid location, must be one of ${LOCATIONS.join(", ")}`);
      return;
    }

    try {
      await exportValveTestHistory({
        siteName,
        startDate: dayjs(startDate),
        location,
        reports: reports ?? [],
      });
      message.success("Valve Test History successfully exported for client");
      onClose();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error exporting Valve Test History:", error);
      message.error("Failed to export Valve Test History");
    }
  };
  return (
    <Modal
      title="Export Client's Valve Test History"
      open={open}
      onCancel={onClose}
      centered
      onOk={onExport}
      okButtonProps={{
        loading: isFetching,
        disabled: isEmpty,
      }}
      okText="Export XLSX"
      width="min(90%, 500px)"
    >
      <FieldWrapper>
        <label htmlFor="site">Site</label>
        <Select
          aria-label="site"
          placeholder="Site"
          id="site"
          onChange={setSelectedSiteName}
          value={siteName}
          showSearch
          options={(data ?? [])
            .filter((site) => !!site.vCustomer)
            .map((site) => ({
              key: site.vCustomer,
              value: site.vCustomer,
              label: site.vCustomer,
            }))}
        />
      </FieldWrapper>
      <FieldWrapper>
        <label htmlFor="years">Select date range</label>
        <Select
          aria-label="years"
          placeholder="Date range"
          id="years"
          onChange={setYears}
          value={years}
          options={[
            {
              key: 1,
              value: 3,
              label: "Valves not tested within last 3 years",
            },
            {
              key: 2,
              value: 5,
              label: "Valves not tested within last 5 years",
            },
            ...(process.env.NODE_ENV !== "production"
              ? [
                  // This option is only shown in non-production environments, its meant for testing purposes to easily generate exports with valves in them without having to manipulate valve test dates in the database
                  {
                    key: 3,
                    value: 1,
                    label: "Valves not tested within last year",
                  },
                ]
              : []),
          ]}
        />
      </FieldWrapper>
      <NotesContainer>
        <PSVExportNote>
          <span>Please note: </span>
          <br />
          This export shows valves overdue for testing. It includes valves that
          should have been tested within the selected date range based on their
          testing interval, but haven&apos;t been. Each valve appears once with
          its most recent published report.
        </PSVExportNote>
        {isEmpty && (
          <NoDataNote>
            The export contains no data. No valves were last tested within the
            selected date range.
          </NoDataNote>
        )}
      </NotesContainer>
    </Modal>
  );
};

export default PSVExportModal;
